import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { ArrowType } from '@/components/game/player/ArrowType';
import { useBowDrawing } from '@/hooks/useBowDrawing';
import { GameStateEnum } from '@/store/gameStateSlice';
import UpgradeMenu from './UpgradeMenu';

const HUDContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 10;
`;

const HealthBar = styled.div`
  position: absolute;
  bottom: 20px;
  left: 20px;
  width: 200px;
  height: 20px;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 10px;
  overflow: hidden;
`;

const HealthFill = styled.div<{ $health: number }>`
  width: ${props => props.$health}%;
  height: 100%;
  background-color: ${props => {
    if (props.$health > 60) return '#4CAF50';
    if (props.$health > 30) return '#FFC107';
    return '#F44336';
  }};
  transition: width 0.3s ease;
`;

const AmmoDisplay = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const AmmoType = styled.div<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  margin-bottom: 5px;
  padding: 5px 10px;
  background-color: ${props => props.$isSelected ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.5)'};
  border-radius: 5px;
  color: white;
  font-weight: ${props => props.$isSelected ? 'bold' : 'normal'};
`;

const AmmoIcon = styled.div<{ $type: string }>`
  width: 20px;
  height: 20px;
  margin-right: 10px;
  border-radius: 50%;
  background-color: ${props => {
    switch (props.$type) {
      case '0': return '#FFFFFF'; // NORMAL
      case '1': return '#FF5722'; // FIRE
      case '2': return '#F44336'; // EXPLOSIVE
      case '3': return '#4CAF50'; // POISON
      default: return '#FFFFFF';
    }
  }};
`;

const AmmoCount = styled.span`
  min-width: 30px;
  text-align: right;
`;

const WaveInfo = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  background-color: rgba(0, 0, 0, 0.5);
  padding: 10px 20px;
  border-radius: 5px;
  color: white;
  text-align: left;
`;

const Crosshair = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 10px;
  height: 10px;
  border: 2px solid white;
  border-radius: 50%;
`;

const DrawStrengthIndicator = styled.div<{ $isDrawing: boolean }>`
  position: absolute;
  bottom: 50px;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  font-size: 12px;
  opacity: ${props => props.$isDrawing ? 1 : 0};
  transition: opacity 0.3s ease;
  z-index: 100;
  pointer-events: none;
`;

const DrawStrengthFill = styled.div<{ $strength: number }>`
  position: absolute;
  top: 5px;
  left: 5px;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: conic-gradient(
    ${props => {
      // Color gradient based on draw strength
      const hue = props.$strength * 120; // 0 = red, 60 = yellow, 120 = green
      return `hsl(${hue}, 100%, 50%) 0% ${props.$strength * 100}%, 
      rgba(0, 0, 0, 0.3) ${props.$strength * 100}% 100%`;
    }}
  );
  z-index: 1;
  transition: transform 0.1s ease;
  transform: ${props => props.$strength >= 0.99 ? 'scale(1.05)' : 'scale(1)'};
  box-shadow: ${props => props.$strength >= 0.99 ? '0 0 10px rgba(255, 255, 0, 0.7)' : 'none'};
`;

const DrawStrengthText = styled.div<{ $strength: number }>`
  position: relative;
  z-index: 2;
  text-align: center;
  font-weight: bold;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
  color: ${props => {
    if (props.$strength >= 0.9) return '#00FF00'; // Green for high draw
    if (props.$strength >= 0.5) return '#FFFF00'; // Yellow for medium draw
    return '#FF5555'; // Red for low draw
  }};
  transform: ${props => props.$strength >= 0.99 ? 'scale(1.1)' : 'scale(1)'};
  transition: transform 0.1s ease, color 0.3s ease;
`;

const DamageText = styled.div<{ $multiplier: number }>`
  position: relative;
  z-index: 2;
  font-size: 10px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
  color: ${props => {
    if (props.$multiplier >= 1.2) return '#00FF00'; // Green for high damage
    if (props.$multiplier >= 0.7) return '#FFFF00'; // Yellow for medium damage
    return '#FF5555'; // Red for low damage
  }};
`;

interface HUDProps {
  gameController: any; // Replace with proper type when available
}

const HUD: React.FC<HUDProps> = ({ gameController }) => {
  // Get data from Redux store
  const playerState = useSelector((state: any) => state.player);
  const waveState = useSelector((state: any) => state.wave);
  const gameState = useSelector((state: RootState) => state.gameState);
  
  // Extract values from state
  const health = playerState?.health || 100;
  const maxHealth = playerState?.maxHealth || 100;
  const healthPercentage = Math.floor((health / maxHealth) * 100);
  const currentWave = waveState?.currentWave || 1;
  const enemiesRemaining = waveState?.enemiesRemaining || 0;
  const selectedArrowType = playerState?.currentArrowType || ArrowType.NORMAL;
  
  // Get arrow inventory from player state
  const arrowInventory = playerState?.arrowInventory || {
    [ArrowType.NORMAL]: 30,
    [ArrowType.FIRE]: 10,
    [ArrowType.EXPLOSIVE]: 5,
    [ArrowType.POISON]: 10
  };
  
  // Convert to array format for rendering
  const arrowInventoryArray = [
    { type: ArrowType.NORMAL, count: arrowInventory[ArrowType.NORMAL] },
    { type: ArrowType.FIRE, count: arrowInventory[ArrowType.FIRE] },
    { type: ArrowType.EXPLOSIVE, count: arrowInventory[ArrowType.EXPLOSIVE] },
    { type: ArrowType.POISON, count: arrowInventory[ArrowType.POISON] }
  ];
  
  // Use the bow drawing hook
  const { isDrawing, drawStrength, arrowType } = useBowDrawing();
  
  // Debug logging for draw state changes
  useEffect(() => {
    console.log('[HUD] Draw state changed:', { isDrawing, drawStrength, arrowType });
  }, [isDrawing, drawStrength, arrowType]);
  
  // Base damage values by arrow type
  const baseDamageValues: Record<number, number> = {
    [ArrowType.NORMAL]: 10,
    [ArrowType.FIRE]: 18,
    [ArrowType.EXPLOSIVE]: 25,
    [ArrowType.POISON]: 12
  };
  
  // Calculate damage multiplier based on draw strength
  const calculateDamageMultiplier = (strength: number) => {
    const minMultiplier = 0.3;
    const maxMultiplier = 1.5;
    const strengthSquared = strength * strength;
    return minMultiplier + (maxMultiplier - minMultiplier) * strengthSquared;
  };
  
  // Calculate estimated damage
  const damageMultiplier = calculateDamageMultiplier(drawStrength);
  const baseDamage = baseDamageValues[arrowType as number] || baseDamageValues[selectedArrowType as number] || 10;
  const estimatedDamage = baseDamage * damageMultiplier;
  
  // Ensure the indicator is only shown when actually drawing
  const showDrawIndicator = isDrawing && drawStrength > 0;
  
  return (
    <>
      <HUDContainer>
        <HealthBar>
          <HealthFill $health={healthPercentage} />
        </HealthBar>
        
        <AmmoDisplay>
          {arrowInventoryArray.map(arrow => (
            <AmmoType 
              key={arrow.type} 
              $isSelected={arrow.type === selectedArrowType}
            >
              <AmmoIcon $type={arrow.type.toString()} />
              <AmmoCount>{arrow.count === -1 ? '∞' : arrow.count}</AmmoCount>
            </AmmoType>
          ))}
        </AmmoDisplay>
        
        <WaveInfo>
          Wave {currentWave} - Enemies Remaining: {enemiesRemaining}
        </WaveInfo>
        
        <DrawStrengthIndicator $isDrawing={showDrawIndicator}>
          <DrawStrengthFill $strength={drawStrength} />
          <DrawStrengthText $strength={drawStrength}>{Math.round(drawStrength * 100)}%</DrawStrengthText>
          <DamageText $multiplier={damageMultiplier}>
            {estimatedDamage.toFixed(1)} dmg ({Math.round(damageMultiplier * 100)}%)
          </DamageText>
        </DrawStrengthIndicator>
        
        <Crosshair />
      </HUDContainer>
      
      {/* Upgrade Menu */}
      {gameState.showUpgradeMenu && (
        <UpgradeMenu gameController={gameController} />
      )}
    </>
  );
};

export default HUD; 